import { useState, useEffect, useReducer, useRef } from 'react';
import { toast } from 'react-toastify';
import { useUser } from 'routes/UserContext';
import { getDailyFormData } from 'api/DailyApi';
import moment from 'moment';
import { getTCPFormById } from 'api/TCPApi';
import { getHAById } from 'api/HaApi';
import { DEFAULT_ORGANIZATION } from 'consts/Organizations';
import useProgressMap from './useProgressMap';
import {
  SET_FORM_DATA,
  SET_GRID_GEOJSON,
  SET_HA_REPORT,
  SET_LOADER,
  SET_TCP_DATA,
  SET_TEAM_ACTIVITY,
  SET_USER_DATA,
  SET_ZONE,
  SET_CROSSHAIR,
  SET_LOCATION_FROM_HA,
} from 'reducers/dailyFormActions';
import dailyFormReducer, { initialState } from 'reducers/dailyFormReducer';
import { EOFoundCategories } from 'consts/Daily';
import { getChoices } from 'api/ChoicesApi';
import axios from 'axios';

export const useDailyFormManagement = (clearanceId, dailyId = null, createData = null) => {
  const [state, dispatch] = useReducer(dailyFormReducer, initialState);
  const { user } = useUser();
  const [eoFoundChoices, setEoFoundChoices] = useState(null);


  const isEmptyType = createData?.type === 'Empty' || clearanceId === 'empty';

  const [readyToLoadMap, setReadyToLoadMap] = useState(false);


  const mapClearanceId = isEmptyType ? null : clearanceId;
  const [gridGeojson, mapLoader, setGridGeojson] = useProgressMap(mapClearanceId, dailyId, null, readyToLoadMap);

  const dailyDataFetched = useRef(false);
  const tcpDataFetched = useRef(false);
  const haDataFetched = useRef(false);
  const userDataSet = useRef(false);
  const loadStarted = useRef(false);

  // useEffect(() => {
  //   (async () => {
  //     const choices = await getChoices('clearance', 'daily-eo-found');
  //     setEoFoundChoices(choices.data);
  //   })();
  // }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadAllData = async () => {
      if (!user) return;

      dispatch({ type: SET_LOADER, payload: true });

      dispatch({
        type: SET_USER_DATA,
        payload: {
          team_id: user?.team_id,
          reporter_name: `${user?.name} ${user?.surname}`,
          reporter_position: user?.reporter_position,
          organization: DEFAULT_ORGANIZATION,
          clearance_task_id: clearanceId,
          report_date: moment().format('DD.MM.YYYY'),
          task_customer: user?.default_customer?.id ?? user?.default_customer ?? null,
        }
      });

      let dailyData = null;
      if (dailyId) {
        try {
          const res = await getDailyFormData(dailyId, controller.signal);
          dailyData = res.data;
          dispatch({ type: SET_FORM_DATA, payload: dailyData });
        } catch (err) {
          if (!axios.isCancel(err)) {
            console.error(err);
            toast.error('Failed to load daily form data');
          }
        }
      }


      if (dailyId) {

        if (dailyData?.info?.ha_report_id && !dailyData?.info?.clearance_task_id) {
          const haId = dailyData.info.ha_report_id;


          try {
            const haResponse = await getHAById(haId, false, false, controller.signal);
            const haData = haResponse.data;

            dispatch({ type: SET_HA_REPORT, payload: haData });
            if (haData?.file_zone) {
              dispatch({ type: SET_ZONE, payload: haData.file_zone });
            }



            const imsmaId = haData?.general?.imsma_id || haData?.info?.general?.imsma_id || haData?.imsma_id;
            if (imsmaId) {

              dispatch({ type: 'SET_HA_IMSMA_ID', payload: imsmaId });
            }
          } catch (err) {
            if (!axios.isCancel(err)) {
              console.error('View mode - failed to load HA data:', err);
            }
          }
        }

        else if (dailyData?.info?.clearance_task_id && !dailyData?.info?.ha_report_id) {
          const tcpId = dailyData.info.clearance_task_id;


          try {
            const tcpResponse = await getTCPFormById(tcpId, controller.signal);
            const tcpData = tcpResponse.data;

            if (tcpData?.imsma_id) {

              dispatch({ type: 'SET_TCP_IMSMA_ID', payload: tcpData.imsma_id });
            }

            // If TCP references HA, load it for polygon and zone
            if (tcpData?.ha_report) {
              const haResponse = await getHAById(tcpData.ha_report, false, false, controller.signal);
              const haData = haResponse.data;
              dispatch({ type: SET_HA_REPORT, payload: haData });
              if (haData?.file_zone) {
                dispatch({ type: SET_ZONE, payload: haData.file_zone });
              }
            }
          } catch (err) {
            if (!axios.isCancel(err)) {
              console.error('View mode - failed to load TCP data:', err);
            }
          }
        }

        // Ensure progress map loads in Edit mode too
        try {
          const finalFormData = dailyData || state.formData;
          const teamActivity = finalFormData?.info?.dms?.team_activity;
          const shouldLoadMap = teamActivity === 'field_work' && !isEmptyType;
          setReadyToLoadMap(shouldLoadMap);
        } catch (_) {}

        dispatch({ type: SET_LOADER, payload: false });
        return;
      }

      const finalClearanceId = createData?.type === 'HA'
      ? createData.subType
      : (dailyData?.info?.clearance_task_id || clearanceId);

      if (createData?.type === 'HA') {
        try {
          const haResponse = await getHAById(finalClearanceId, false, false, controller.signal);
          const haData = haResponse.data;
          dispatch({
            type: SET_HA_REPORT,
            payload: { ...haData, ha_report_id: finalClearanceId }
          });
          if (haData?.info?.location) {
            dispatch({
              type: SET_LOCATION_FROM_HA,
              payload: haData.info.location
            });
          }

          const utmZone = haData?.file_zone || "37U";
          dispatch({ type: SET_ZONE, payload: utmZone });
          dispatch({ type: 'SET_CLEARANCE_TASK_ID', payload: null });
          dispatch({ type: 'SET_HA_REPORT_ID', payload: finalClearanceId });

          const imsmaId = haData?.general?.imsma_id || haData?.info?.general?.imsma_id || haData?.imsma_id;
          if (imsmaId) {
            dispatch({ type: 'SET_HA_IMSMA_ID', payload: imsmaId });
          }
        } catch (err) {
          if (!axios.isCancel(err)) {
            console.error(err);
            toast.error('Failed to load HA data');
          }
        }
      } else if (createData?.type !== 'HA' && !isEmptyType && finalClearanceId) {
        try {
          const tcpRes = await getTCPFormById(finalClearanceId, controller.signal);
          const tcpData = tcpRes.data;

          dispatch({
            type: SET_TCP_DATA,
            payload: {
              donor: tcpData?.donor,
              clearance_task_imsma_id: tcpData?.imsma_id || tcpData?.internal_id,
              ha_report_id: null,
              preserveExisting: true
            }
          });

          if (tcpData?.crosshair) {
            dispatch({ type: SET_CROSSHAIR, payload: tcpData?.crosshair });
          }

          if (tcpData?.ha_report) {
            const haResponse = await getHAById(tcpData.ha_report, false, false, controller.signal);
            dispatch({ type: SET_HA_REPORT, payload: haResponse.data });
            if (haResponse?.data?.info?.location) {
              dispatch({
                type: SET_LOCATION_FROM_HA,
                payload: haResponse.data.info.location
              });
            }
            dispatch({ type: SET_ZONE, payload: haResponse.data?.file_zone });
          }
        } catch (err) {
          if (!axios.isCancel(err)) {
            console.error(err);
            toast.error('Failed to load TCP/HA data');
          }
        }
      }

      const finalFormData = dailyData || state.formData;
      const teamActivity = finalFormData?.info?.dms?.team_activity;
      const shouldLoadMap = teamActivity === 'field_work' && !isEmptyType;
      setReadyToLoadMap(shouldLoadMap);

      dispatch({ type: SET_LOADER, payload: false });
    };

    loadAllData();

    return () => {
      controller.abort();
    };
  }, [user, clearanceId, dailyId, isEmptyType, dispatch, createData]);

  useEffect(() => {
    const teamActivity = state?.formData?.info?.dms?.team_activity;
    const shouldLoadMap = teamActivity === 'field_work' && !isEmptyType;
    setReadyToLoadMap(shouldLoadMap);
  }, [state?.formData?.info?.dms?.team_activity, isEmptyType]);

  useEffect(() => {
    if (state.originalGridGeojson?.features?.length > 0) return;
    if (gridGeojson && gridGeojson?.features?.length > 0) {
      dispatch({
        type: SET_GRID_GEOJSON,
        payload: gridGeojson
      });
    }
  }, [gridGeojson, state?.originalGridGeojson]);

  const handleTeamActivity = (value) => {
    dispatch({
      type: SET_TEAM_ACTIVITY,
      payload: value,
      preserveFieldWork: true
    });
  };

  return {
    formData: state?.formData,
    loader: state.loader,
    gridGeojson,
    originalGridGeojson: state.originalGridGeojson,
    mapLoader,
    haReport: state.haReport,
    coords3857: state.coords3857,
    zone: state.zone,
    setGridGeojson,
    dispatch,
    isLocationLoaded: state.isLocationLoaded,
    crosshair: state.crosshair,
    eoFoundChoices,
  };
}